class Emitter {
    listeners = {};
    on(event, fn) {
        (this.listeners[event] ??= new Set()).add(fn);
        return () => this.listeners[event]?.delete(fn);
    }
    emit(event, value) {
        for (const fn of this.listeners[event] ?? [])
            fn(value);
    }
}
export class LiveSocket extends Emitter {
    opts;
    ws = null;
    attempts = 0;
    closedByUser = false;
    ping = null;
    retry = null;
    state = "connecting";
    constructor(opts) {
        super();
        this.opts = opts;
    }
    open() {
        this.closedByUser = false;
        this.connect();
    }
    close() {
        this.closedByUser = true;
        if (this.retry)
            clearTimeout(this.retry);
        if (this.ping)
            clearInterval(this.ping);
        this.ws?.close(1000, "bye");
        this.setState("closed");
    }
    send(msg) {
        if (this.state !== "ready" || !this.ws || this.ws.readyState !== 1)
            return false;
        this.ws.send(JSON.stringify(msg));
        return true;
    }
    setState(s) {
        if (this.state === s)
            return;
        this.state = s;
        this.emit("state", s);
    }
    connect() {
        this.setState("connecting");
        const ws = new this.opts.WebSocket(this.opts.url);
        this.ws = ws;
        ws.onopen = () => ws.send(JSON.stringify(this.opts.auth?.() ??
            { type: "auth", token: this.opts.token ?? "", role: this.opts.role }));
        ws.onmessage = (e) => {
            let msg;
            try {
                msg = JSON.parse(String(e.data));
            }
            catch {
                return;
            }
            if (msg.type === "ready") {
                this.attempts = 0;
                this.setState("ready");
                if (this.ping)
                    clearInterval(this.ping);
                this.ping = setInterval(() => this.send({ type: "ping" }), this.opts.pingMs ?? 10_000);
            }
            else if (msg.type === "session")
                this.emit("session", msg);
            else if (msg.type === "signal")
                this.emit("signal", msg);
            else if (msg.type === "notification")
                this.emit("notification", msg);
            else if (msg.type === "presence")
                this.emit("presence", msg);
            else if (msg.type === "error")
                this.emit("error", { code: msg.code, message: msg.message });
        };
        ws.onerror = () => undefined;
        ws.onclose = (e) => {
            if (this.ping)
                clearInterval(this.ping);
            if (this.closedByUser)
                return;
            // 4403: the interview room is not open (or no longer); 4409: the same room was joined elsewhere.
            if (e.code === 4403 || e.code === 4409) {
                this.emit("error", { code: e.code === 4403 ? "closed" : "replaced", message: "" });
                this.setState("closed");
                return;
            }
            if (e.code === 4401) {
                this.emit("error", { code: "unauthenticated", message: "انتهت الجلسة، سجّل الدخول مرة أخرى" });
                this.setState("closed");
                return;
            }
            this.setState("offline");
            const delay = Math.min(10_000, 500 * 2 ** this.attempts++);
            this.retry = setTimeout(() => this.connect(), delay);
        };
    }
}
const AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
export function gradeQuality(rttMs, loss, jitterMs) {
    const r = rttMs ?? 0;
    const l = loss ?? 0;
    const j = jitterMs ?? 0;
    if (r > 600 || l > 0.1 || j > 100)
        return "poor";
    if (r > 300 || l > 0.03 || j > 50)
        return "fair";
    return "good";
}
export class LiveCall extends Emitter {
    opts;
    pc = null;
    local = null;
    makingOffer = false;
    ignoreOffer = false;
    pendingCandidates = [];
    statsTimer = null;
    lastBytes = 0;
    lastLost = 0;
    lastReceived = 0;
    badSamples = 0;
    reportedOk = null;
    unsub = [];
    studentTracksAdded = false;
    /** hangup() was called: a start() still waiting on the microphone or ICE servers stops there. */
    stopped = false;
    state = {
        phase: "idle",
        muted: false,
        receiving: false,
        quality: null,
        rttMs: null,
        loss: null,
        jitterMs: null,
        candidateType: null,
        bytesReceived: 0,
        audioLevel: null,
        error: null,
    };
    constructor(opts) {
        super();
        this.opts = opts;
    }
    get polite() {
        return this.opts.side === "student";
    }
    set(patch) {
        this.state = { ...this.state, ...patch };
        this.emit("state", this.state);
    }
    /** Acquire the microphone, create the connection and (teacher) send the first offer. */
    async start() {
        if (this.pc || this.stopped)
            return;
        this.set({ phase: "starting", error: null });
        try {
            this.local = await this.opts.platform.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false });
            if (this.stopped)
                return this.stopLocal();
        }
        catch (err) {
            const name = err?.name ?? "";
            this.set({
                phase: "failed",
                error: name === "NotAllowedError" || name === "SecurityError" ? "mic_denied" : "mic_unavailable",
            });
            return;
        }
        let ice;
        try {
            ice = await this.opts.getIceServers();
        }
        catch {
            this.stopLocal();
            if (!this.stopped)
                this.set({ phase: "failed", error: "ice_servers" });
            return;
        }
        if (this.stopped)
            return this.stopLocal();
        const pc = new this.opts.platform.RTCPeerConnection({
            iceServers: ice.iceServers,
            ...(this.opts.relayOnly ? { iceTransportPolicy: "relay" } : {}),
        });
        this.pc = pc;
        this.set({ phase: "connecting" });
        pc.onicecandidate = ({ candidate }) => {
            this.signal({
                candidate: candidate
                    ? typeof candidate.toJSON === "function"
                        ? candidate.toJSON()
                        : candidate
                    : null,
            });
        };
        pc.ontrack = (e) => {
            const stream = e.streams[0];
            if (stream)
                this.emit("remoteStream", stream);
        };
        pc.onnegotiationneeded = () => void this.negotiate();
        const onState = () => this.onConnectionState();
        pc.oniceconnectionstatechange = onState;
        if ("onconnectionstatechange" in pc)
            pc.onconnectionstatechange = onState;
        this.unsub.push(this.opts.socket.on("signal", (m) => {
            if (m.sessionId === this.opts.sessionId)
                void this.onSignal(m.data);
        }), this.opts.socket.on("state", (s) => {
            if (s !== "ready")
                return;
            // While this socket was away the server marked our media as not ok (socket_closed); say again
            // how it is, or the session stays "reconnecting" although the audio is back.
            this.reportedOk = null;
            // The teacher restarts ICE in case the network path changed.
            if (this.opts.side === "teacher" && this.state.phase !== "connected")
                this.restartIce();
        }), 
        // A status change (e.g. a resume) resets the server's media flags: report afresh.
        this.opts.socket.on("session", (m) => {
            if (m.id === this.opts.sessionId)
                this.reportedOk = null;
        }));
        // The teacher adds the microphone first, which triggers the offer. The student adds it after the offer arrives.
        if (this.opts.side === "teacher")
            this.addLocalTracks();
        this.statsTimer = setInterval(() => void this.sample(), this.opts.statsIntervalMs ?? 2_000);
    }
    setMuted(muted) {
        for (const t of this.local?.getAudioTracks() ?? [])
            t.enabled = !muted;
        this.set({ muted });
    }
    /** Close the connection and release the microphone. Does not end the session on the server. */
    hangup() {
        this.stopped = true;
        if (this.statsTimer)
            clearInterval(this.statsTimer);
        for (const u of this.unsub)
            u();
        this.unsub = [];
        this.pc?.close();
        this.pc = null;
        this.stopLocal();
        this.set({ phase: "closed", receiving: false });
    }
    stopLocal() {
        for (const t of this.local?.getTracks() ?? [])
            t.stop();
        this.local = null;
    }
    addLocalTracks() {
        if (!this.pc || !this.local)
            return;
        for (const t of this.local.getAudioTracks())
            this.pc.addTrack(t, this.local);
    }
    signal(data) {
        this.opts.socket.send({ type: "signal", sessionId: this.opts.sessionId, data });
    }
    async negotiate(iceRestart = false) {
        const pc = this.pc;
        if (!pc)
            return;
        // The student never starts negotiation; it only answers (and is the polite peer on collisions).
        if (this.opts.side === "student")
            return;
        try {
            this.makingOffer = true;
            const offer = await pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
            if (pc.signalingState !== "stable")
                return;
            await pc.setLocalDescription(offer);
            const d = pc.localDescription;
            if (d)
                this.signal({
                    description: { type: d.type, sdp: d.sdp },
                    ...(iceRestart ? { restart: true } : {}),
                });
        }
        catch {
            this.set({ error: "negotiation" });
        }
        finally {
            this.makingOffer = false;
        }
    }
    restartIce() {
        if (!this.pc)
            return;
        if (typeof this.pc.restartIce === "function")
            this.pc.restartIce();
        else
            void this.negotiate(true);
    }
    async onSignal(data) {
        const pc = this.pc;
        if (!pc)
            return;
        try {
            if (data.description) {
                const d = data.description;
                const collision = d.type === "offer" && (this.makingOffer || pc.signalingState !== "stable");
                this.ignoreOffer = !this.polite && collision;
                if (this.ignoreOffer)
                    return;
                await pc.setRemoteDescription(d);
                for (const c of this.pendingCandidates.splice(0))
                    await pc.addIceCandidate(c).catch(() => undefined);
                if (d.type === "offer") {
                    if (this.opts.side === "student" && !this.studentTracksAdded) {
                        this.addLocalTracks();
                        this.studentTracksAdded = true;
                    }
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    const ld = pc.localDescription;
                    if (ld)
                        this.signal({ description: { type: ld.type, sdp: ld.sdp } });
                }
            }
            else if (data.candidate !== undefined) {
                if (data.candidate === null)
                    return; // end of candidates
                if (!pc.remoteDescription) {
                    this.pendingCandidates.push(data.candidate);
                    return;
                }
                await pc.addIceCandidate(data.candidate).catch((err) => {
                    if (!this.ignoreOffer)
                        throw err;
                });
            }
        }
        catch {
            this.set({ error: "negotiation" });
        }
    }
    onConnectionState() {
        const pc = this.pc;
        if (!pc)
            return;
        const s = pc.connectionState ?? pc.iceConnectionState;
        if (s === "connected" || s === "completed")
            this.set({ phase: "connected" });
        else if (s === "disconnected") {
            this.set({ phase: "reconnecting" });
            if (this.opts.side === "teacher")
                setTimeout(() => this.state.phase === "reconnecting" && this.restartIce(), 1_500);
        }
        else if (s === "failed") {
            this.set({ phase: "reconnecting" });
            if (this.opts.side === "teacher")
                this.restartIce();
        }
    }
    /** Read WebRTC stats, update quality, and report media health to the server when it changes. */
    async sample() {
        const pc = this.pc;
        if (!pc)
            return;
        let bytes = 0;
        let lost = 0;
        let received = 0;
        let jitter = null;
        let level = null;
        let rtt = null;
        let localCandidateId = null;
        const candidates = new Map();
        try {
            const stats = await pc.getStats();
            stats.forEach((r) => {
                if (r.type === "inbound-rtp" && (r.kind === "audio" || r.mediaType === "audio")) {
                    bytes += Number(r.bytesReceived ?? 0);
                    lost += Number(r.packetsLost ?? 0);
                    received += Number(r.packetsReceived ?? 0);
                    if (typeof r.jitter === "number")
                        jitter = r.jitter * 1000;
                    if (typeof r.audioLevel === "number")
                        level = r.audioLevel;
                }
                else if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded") {
                    if (typeof r.currentRoundTripTime === "number")
                        rtt = r.currentRoundTripTime * 1000;
                    localCandidateId = r.localCandidateId ?? null;
                }
                else if (r.type === "local-candidate") {
                    candidates.set(r.id, r.candidateType ?? "");
                }
            });
        }
        catch {
            return;
        }
        const receiving = bytes > this.lastBytes;
        const dLost = Math.max(0, lost - this.lastLost);
        const dRecv = Math.max(0, received - this.lastReceived);
        const loss = dLost + dRecv > 0 ? dLost / (dLost + dRecv) : null;
        this.lastBytes = bytes;
        this.lastLost = lost;
        this.lastReceived = received;
        const connected = ["connected", "completed"].includes(pc.connectionState ?? pc.iceConnectionState);
        const ok = connected && receiving;
        this.badSamples = ok ? 0 : this.badSamples + 1;
        this.set({
            receiving,
            bytesReceived: bytes,
            audioLevel: level,
            rttMs: rtt,
            loss,
            jitterMs: jitter,
            quality: connected ? gradeQuality(rtt, loss, jitter) : null,
            candidateType: localCandidateId ? (candidates.get(localCandidateId) ?? null) : this.state.candidateType,
        });
        // Report "ok" at once; report a drop only after two bad samples so brief hiccups do not flap the session.
        const report = ok ? true : this.badSamples >= 2 ? false : this.reportedOk;
        if (report !== null && report !== this.reportedOk) {
            const sent = this.opts.socket.send({
                type: "media",
                sessionId: this.opts.sessionId,
                ok: report,
                stats: { rttMs: rtt, loss, jitterMs: jitter, candidateType: this.state.candidateType },
            });
            if (sent)
                this.reportedOk = report;
        }
    }
}
//# sourceMappingURL=index.js.map